<template>
    <div id="app-wrapper" :class="{ initialised }">
        <header-navbar v-if="initialised" />
        <teleport v-if="initialised" defer to="#header-sidebar-visibility-toggle">
            <prime-button
                v-if="showSidebarHeaderToggle"
                text
                type="button"
                class="sidebar-visibility-toggle-btn"
                @click.stop="toggleSidebarVisibility"
                :aria-label="uiStore.showNavSidebar ? 'Hide sidebar' : 'Show sidebar'"
                :title="uiStore.showNavSidebar ? 'Hide sidebar' : 'Show sidebar'"
            >
                <font-awesome-icon :icon="['fas', uiStore.showNavSidebar ? 'chevron-left' : 'chevron-right']" />
            </prime-button>
        </teleport>
        <div
            id="app-view"
            :class="{ 'sidebar-grid': isSidebarPinned, expanded: isSidebarPinned && uiStore.showNavSidebar }"
        >
            <nav-sidebar v-if="routeHasSidebar && sideNavVisibleSetting !== undefined" />
            <router-wrapper :enactInitialised="enactInitialised" :initialised="initialised" :showNoAccess="!blockTopLevelNoAccess" />
            <central-modal-wrapper v-if="!uiStore.showPageLoader" />
            <site-area-relevance-prompt-modal v-if="!uiStore.showPageLoader" />
            <landscape-recommendation />
            <primevue-toast :on-click="handleToastMessageClick" />
            <confirm-dialog>
                <template #container="{ message, acceptCallback, rejectCallback }">
                    <div class="confirm-dialog">
                        <span class="confirm-message" v-html="message.message" />
                        <div class="confirm-button-row">
                            <prime-button class="plain-btn" type="button" @click="rejectCallback">
                                No
                                <font-awesome-icon :icon="['fas', 'times']" />
                            </prime-button>
                            <prime-button type="button" @click="acceptCallback">
                                Yes
                                <font-awesome-icon :icon="['fas', 'check']" />
                            </prime-button>
                        </div>
                    </div>
                </template>
            </confirm-dialog>
        </div>
        <global-notification v-if="showGlobalNotification" />
        <access-denied
            v-model:visible="alertStore.showAccessDeniedModal"
        />
        <delta-terms-dialog
            :closable="userAccessStore.userAccess.research?.acceptedDeltaGptTerms"
            :visible="showDeltaTermsModal"
            @accept="acceptDeltaTerms"
            @decline="declineTerms"
        />
        <enact-terms-dialog
            :closable="userAccessStore.userAccess.enact.acceptedEnactTerms"
            :visible="showEnactTermsModal"
            @accept="acceptEnactTerms"
            @decline="declineTerms"
        />
        <konami-spark-dialog v-model:visible="showKonamiGameSelector" />
        <running-as-different-user-control
            v-if="userAccessStore.isRunningAsDifferentUser"
            @stop="stopRunningAsDifferentUser"
        />
    </div>
</template>

<script setup lang="ts">
import PrimevueToast from 'primevue/toast';
import { useToast } from 'primevue/usetoast';
import {
    computed,
    onMounted,
    ref,
    watch,
} from 'vue';

import Highcharts from 'highcharts';
import { authState } from '@lcp/http-auth';
import { useRoute } from 'vue-router';
import { useHead, useSeoMeta } from '@unhead/vue';
import PrimeButton from 'primevue/button';
import ConfirmDialog from 'primevue/confirmdialog';
import CentralModalWrapper from '@/components/common/CentralModalWrapper.vue';
import SiteAreaRelevancePromptModal from '@/components/community/SiteAreaRelevancePromptModal.vue';
import AccessDenied from '@/components/common/AccessDenied.vue';
import { useAlertStore } from '@/stores/alertStore.ts';
import { useUIStore } from '@/stores/uiStore.ts';
import HeaderNavbar from '@/components/layout/HeaderNavbar.vue';
import LandscapeRecommendation from '@/components/common/LandscapeRecommendation.vue';
import { IBrowserNotificationPayload } from '@/interfaces/powerMarkets/notifications/IBrowserNotificationPayload.ts';
import { handleBrowserNotificationSound } from '@/utils/notifications/notificationAudio.ts';
import DeltaTermsDialog from './components/layout/DeltaTermsDialog.vue';
import EnactTermsDialog from './components/layout/EnactTermsDialog.vue';
import KonamiSparkDialog from './components/layout/KonamiSparkDialog.vue';
import RunningAsDifferentUserControl from './components/layout/RunningAsDifferentUserControl.vue';
import NavSidebar from './components/layout/sidebar/NavSidebar.vue';
import { chartGlobalSettings } from './utils/chartGlobalSettings.ts';
import GlobalNotification from './components/common/GlobalNotification.vue';
import { useDeltaUserSettingsStore } from './stores/deltaGpt/deltaGptUserSettingsStore.ts';
import { useUserAccessStore } from './stores/userAccessStore.ts';
import { SignalRConnection } from './realTimeConnection/SignalRConnection.ts';
import type { SignalRMode } from './realTimeConnection/SignalRConnection.ts';
import { subscribeToOtherTabUserSettingsChange } from './realTimeConnection/broadcastChannel.ts';
import { useUserSettingsActions } from './actions/powerMarkets/userSettingsActions.ts';
import { adminApi } from './api/admin/adminApi.ts';
import { refreshUserCaches } from './helpers/admin/adminHelper.ts';
import { deltaGptApi } from './api/deltaGpt/deltaGptApi.ts';
import RouterWrapper from './RouterWrapper.vue';
import { router } from './router.ts';
import { authApi } from './api/authApi.ts';
import { useDataStoreActions } from './actions/powerMarkets/dataStoreActions.ts';
import { useDataStore } from './stores/powerMarkets/dataStore.ts';
import { useDataHubStore } from './stores/powerMarkets/dataHubStore.ts';
import { enactApiBaseKeyValues, TApiBaseUrlKey } from './api/useApi.ts';
import { useUserSettingsStore } from './stores/userSettingsStore.ts';
import { useKonamiCode } from './composables/useKonamiCode.ts';
import { SPARK_SITE_URL } from './constants/SparkSite.ts';
import { getFallbackColourPaletteId, isColourPaletteId } from './constants/ColourPalettes.ts';

const isProd = import.meta.env.MODE === 'production';

const uiStore = useUIStore();
const dataHubStore = useDataHubStore();
const userAccessStore = useUserAccessStore();
const deltaUserSettingsStore = useDeltaUserSettingsStore();
const alertStore = useAlertStore();
const userSettingsStore = useUserSettingsStore();
const route = useRoute();
const userSettingsActions = useUserSettingsActions();
const dataStoreActions = useDataStoreActions();
const dataStore = useDataStore();
const toast = useToast();

const initialised = ref<boolean>(false);
const enactInitialised = ref<boolean>(false);
const showKonamiGameSelector = ref<boolean>(false);

const currentRouteIsEnact = computed<boolean>(() => !!route.matched.find(r => r.meta.isEnact));
const userHasShortTermPowerAccess = computed<boolean>(() => userAccessStore.shortTermMarketsConfig?.hasPaidShortTermMarketsServices ?? false);
const desiredSignalRMode = computed<SignalRMode>(() => {
    if (authState.value.loggedIn && userHasShortTermPowerAccess.value) return 'authenticated';
    if (route.name === 'currentNews') return 'free';
    return null;
});
const desiredSignalRBearerToken = computed<string | null>(() => (desiredSignalRMode.value === 'authenticated'
    ? (authState.value as { token?: string })?.token ?? null
    : null));
const routeHasSidebar = computed<boolean>(() => !!route.matched.find(r => r.meta.sidebar));
const sideNavVisibleSetting = computed<boolean | undefined>(() => userSettingsStore.userSettings?.otherSettings.data.sideNavVisible);
const isMobileView = computed<boolean>(() => uiStore.isMobileView);

const isSidebarPinned = computed<boolean>(() => routeHasSidebar.value && sideNavVisibleSetting.value === true);

const showSidebarHeaderToggle = computed<boolean>(() => routeHasSidebar.value && (!sideNavVisibleSetting.value || isMobileView.value));

const showGlobalNotification = computed<boolean>(() => alertStore.globalNotificationsToShow.length > 0);

const showDeltaTermsModal = computed<boolean>(() => (authState.value.loggedIn
    && !userAccessStore.userAccess.research?.acceptedDeltaGptTerms
    && (route.fullPath.includes('research')
        || route.matched.some(record => String(record.name)?.includes('forecastReport'))
    )
));

const showEnactTermsModal = computed<boolean>(() => (authState.value.loggedIn
    && !userAccessStore.userAccess.enact.acceptedEnactTerms
    && currentRouteIsEnact.value
    && !uiStore.showPageLoader
));

const blockTopLevelNoAccess = computed<boolean>(() => {
    const matchedRouteMetas = route.matched.map(r => r.meta);
    return matchedRouteMetas?.some(meta => meta.blockTopLevelNoAccess) ?? false;
});

function toggleSidebarVisibility (): void {
    uiStore.updateShowNavSidebar(!uiStore.showNavSidebar);
}

function requestBrowserNotificationPermission (): void {
    if (typeof window !== 'undefined' && 'Notification' in window && window.Notification.permission !== 'granted') {
        window.Notification.requestPermission();
    }
}

function handleToastMessageClick (event: { message?: { data?: { clickEventUrl?: string } } }): void {
    const url = event?.message?.data?.clickEventUrl;
    if (url) router.push(url);
}

function showBrowserNotification (alert: IBrowserNotificationPayload): void {
    const title = alert.browserPushSubscriptionName ?? 'Notification';
    const body = alert.data?.hoverOver ?? '';
    const clickEventUrl = alert.data?.clickEventUrl ?? '';
    requestBrowserNotificationPermission();
    handleBrowserNotificationSound(alert);
    if (!alert.browser) return;

    if (!document.hidden) {
        toast.add({
            severity: 'success',
            summary: title,
            detail: body,
            life: 10000,
            ...(clickEventUrl && { data: { clickEventUrl } }),
        } as Parameters<typeof toast.add>[0]);
    } else if (typeof window !== 'undefined' && 'Notification' in window && window.Notification?.permission === 'granted') {
        const n = new window.Notification(title, { body, icon: '/favicon.ico' });
        n.onclick = (): void => {
            window.focus();
            if (clickEventUrl) window.open(`${window.location.origin}${clickEventUrl}`, '_blank');
            n.close();
        };
        setTimeout(() => n.close(), 10000);
    }
}

async function stopRunningAsDifferentUser (): Promise<void> {
    await adminApi.stopRunningAsDifferentUser();
    await refreshUserCaches();
    window.location.reload();
}

async function acceptDeltaTerms (): Promise<void> {
    try {
        await deltaGptApi.acceptTermsAndConditions();
    } catch {
        toast.add({
            severity: 'error', summary: 'Error accepting terms and conditions', detail: 'An error occurred while accepting terms and conditions', life: 5000,
        });
        return;
    }
    userAccessStore.userAccess.research.acceptedDeltaGptTerms = true;
    toast.add({
        severity: 'success', summary: 'Terms and conditions accepted', detail: 'Terms and conditions accepted', life: 5000,
    });
    await deltaGptApi.refreshUserAccess();
    setTimeout(() => {
        window.location.reload();
    }, 1000);
}

async function acceptEnactTerms (): Promise<void> {
    await authApi.acceptEnactTerms().then(async () => {
        userAccessStore.userAccess.enact.acceptedEnactTerms = true;
        toast.add({
            severity: 'success', summary: 'Terms and conditions accepted', detail: 'Terms and conditions accepted', life: 5000,
        });
    }).catch(error => {
        toast.add({
            severity: 'error', summary: 'Error accepting terms and conditions', detail: error.message, life: 5000,
        });
    });
}

function declineTerms (): void {
    toast.add({
        severity: 'warn', summary: 'Terms and conditions declined', detail: 'You have been redirected back to home.', life: 5000,
    });
    router.push({ name: 'Home' });
}

async function initialise (): Promise<void> {
    uiStore.setPageLoader(true);
    Highcharts.setOptions(chartGlobalSettings);
    const hasHydratedColourPalette = uiStore.hydrateColourPaletteFromStorage();
    if (!hasHydratedColourPalette) {
        uiStore.setCssVariables();
    }

    if (authState.value.loggedIn) {
        const wholeSiteTasks: Array<Promise<void>> = [initialiseUserAccessAndShortTermMarkets(), deltaUserSettingsStore.getUserSettings()];
        await Promise.allSettled(wholeSiteTasks);
    }

    await SignalRConnection.sync(desiredSignalRMode.value, desiredSignalRBearerToken.value);
    if (desiredSignalRMode.value === 'authenticated') {
        SignalRConnection.addUserBrowserSubscriptions();
    }

    const isDarkMode = deltaUserSettingsStore.userSettings?.isDarkMode ?? true;
    const serverColourPaletteId = deltaUserSettingsStore.userSettings?.colourPaletteId;
    if (isColourPaletteId(serverColourPaletteId)) {
        uiStore.applyColourPalette(serverColourPaletteId, isDarkMode);
    } else if (!hasHydratedColourPalette) {
        uiStore.applyColourPalette(getFallbackColourPaletteId(isDarkMode), isDarkMode);
    }

    initialised.value = true;

    if (!currentRouteIsEnact.value || (enactInitialised.value && !route.meta.pageNeedsLoader)) {
        uiStore.setPageLoader(false);
    }
}

async function initialiseUserAccessAndShortTermMarkets (): Promise<void> {
    const { shortTermPowerAccessTask, allAccessTask } = userAccessStore.startAccessLoading();
    await Promise.all([
        allAccessTask,
        initialiseShortTermMarkets(shortTermPowerAccessTask),
    ]);
}

async function initialiseShortTermMarkets (shortTermPowerAccessTask: Promise<void>): Promise<void> {
    await shortTermPowerAccessTask;
    if (!userHasShortTermPowerAccess.value) return;
    await Promise.all([
        userSettingsActions.fetchUserSettings(),
        dataStore.fetchStaticStore(),
    ]);
    subscribeToOtherTabUserSettingsChange();
}

if (isProd) {
    useHead({
        meta: [
            { name: 'robots', content: 'index, follow' },
        ],
        link: [
            { rel: 'canonical', href: `${SPARK_SITE_URL}/` },
            { rel: 'sitemap', type: 'application/xml', href: '/sitemap.xml' },
        ],
    });
} else {
    useHead({
        meta: [
            { name: 'robots', content: 'noindex, nofollow, noarchive, nosnippet' },
        ],
    });
}

useHead({
    htmlAttrs: { lang: 'en-GB' },
    link: [
        { rel: 'icon', href: '/favicon.ico', sizes: 'any' },
        {
            rel: 'icon', type: 'image/png', href: '/favicon-96x96.png', sizes: '96x96',
        },
        {
            rel: 'icon', type: 'image/svg+xml', href: '/favicon.svg', sizes: 'any',
        },
        { rel: 'shortcut icon', href: '/favicon.ico' },
        { rel: 'apple-touch-icon', sizes: '180x180', href: '/apple-touch-icon.png' },
        { rel: 'manifest', href: '/site.webmanifest' },
        { rel: 'mask-icon', href: '/safari-pinned-tab.svg', color: uiStore.cssThemeVariables.accentColour },
    ],
    meta: [
        { name: 'theme-color', content: uiStore.cssThemeVariables.accentColour },
        { name: 'color-scheme', content: 'light dark' },
        { name: 'apple-mobile-web-app-title', content: 'Spark' },
        { name: 'viewport', content: 'width=device-width, initial-scale=1' },
        { name: 'msapplication-TileColor', content: uiStore.cssThemeVariables.accentColour },
        { name: 'msapplication-TileImage', content: '/mstile-150x150.png' },
    ],
});

useSeoMeta({
    ogType: 'website',
    ogLocale: 'en_GB',
    ogSiteName: 'Spark by LCP Delta',
});

onMounted(() => {
    requestBrowserNotificationPermission();
});

watch(currentRouteIsEnact, async (newValue) => {
    if (!newValue || enactInitialised.value) return;
    uiStore.setPageLoader(true);
    const promisesList: Array<Promise<void | boolean>> = [
        dataStoreActions.getPeriodData(),
        dataStore.fetchPlantStore(),
    ];

    Object.entries(enactApiBaseKeyValues).forEach(([key]) => {
        const endpoint = key as TApiBaseUrlKey;
        promisesList.push(dataStore.fetchPlantsByEndpoint(endpoint));
    });

    await Promise.allSettled(promisesList);
    userSettingsActions.replaceNonEuCountry();

    enactInitialised.value = true;

    const accessName = route.meta?.accessName;
    const hasAccess = !accessName || userAccessStore.enactPageAccess[accessName]?.hasAccess;

    if ((!route.meta.pageNeedsLoader || !hasAccess) && initialised.value) {
        uiStore.setPageLoader(false);
    }
}, { immediate: true });

watch(
    desiredSignalRMode,
    async () => {
        if (!initialised.value) return;
        await SignalRConnection.sync(desiredSignalRMode.value, desiredSignalRBearerToken.value);
    },
);

watch(
    () => dataHubStore.browserNotifications,
    (newVal) => {
        const alert = newVal[newVal.length - 1];
        if (alert) showBrowserNotification(alert);
    },
    { deep: true },
);
useKonamiCode(() => {
    toast.add({
        severity: 'success',
        summary: 'You found a Spark!',
        detail: '⚡',
        life: 3000,
    });
    showKonamiGameSelector.value = true;
});

initialise();

</script>

<style lang="scss">
@use './assets/scss/components/pdf.scss';

#app {
    background: var(--neutral0);
    overflow-x: hidden;
    overflow-y: hidden;
    height: 100vh;
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: 1fr;

    #app-wrapper {
        display: grid;
        grid-template-columns: 1fr;
        height: 100%;
        overflow: hidden;

        &.initialised {
            grid-template-rows: auto 1fr;
        }
    }
    #app-view {
        display: grid;
        grid-template-columns: 1fr;
        grid-template-rows: 1fr;
        overflow-x: hidden;
        overflow-y: hidden;
        position: relative;
        width: 100%;

        &.sidebar-grid {
            grid-template-columns: var(--sidebarWidth) 1fr;

             @include responsive('sm') {
                // On mobile the sidebar becomes an overlay drawer (see NavSidebar.vue).
                // Keep the grid placement stable (sidebar column stays at 0).
                grid-template-columns: 0 1fr;

                .sidebar-container {
                    width: 0;
                }
            }
        }
    }
}

.confirm-dialog {
    display: flex;
    flex-direction: column;
    gap: var(--extraLargeSpacing);
    padding: var(--largeSpacing) var(--largeSpacing) var(--defaultSpacing);

    .confirm-message {
        font-size: var(--defaultFontSize);
    }
}

.confirm-message {
    font-size: var(--mediumFontSize);
}

.confirm-button-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--defaultSpacing);
}

.sidebar-visibility-toggle-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 30px;
    width: 30px;
    padding: 0;
    color: var(--primaryFont);

    @include responsive(sm) {
        height: auto;
        width: auto;
        padding: var(--defaultSpacing);
    }
}

.sidebar-visibility-toggle-btn:hover {
    color: var(--accentColour);

    @include responsive(sm) {
        color: var(--primaryFont);
    }
}

</style>
